import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Chip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CloseIcon from "@mui/icons-material/Close";
import axios from "../../../config/axiosConfig";
import MuiAlert from "@mui/material/Alert";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";

// Estado 1-2 (Emitido/Detalle) de una OT de Venta de Equipos Activos (tipo_ot=14):
// espejo de index.php + cargaDetalle.php + guarda.php de ot_venta_activos/ — DISTINTO de
// VenderEquipoOT.jsx (tipo_ot=11, Venta de Equipo): acá se vende el equipo que el
// cliente YA tiene Activo/en servicio, no equipo Inactivo de stock. Por eso el catálogo
// filtra `estado=Activo` + `cod_servicio` (equipo de ESTE cliente, no todo el stock de
// la sucursal) y el guardado es directo a 'Vendido Activo' en un solo paso — no hay
// activación posterior.
const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const tableSx = { "& .MuiTableCell-root": { py: 0.1, px: 0.6, fontSize: 10.5, lineHeight: 1.3 } };
const controlSx = {
  "& .MuiInputLabel-root": { fontSize: 11 },
  "& .MuiOutlinedInput-root": { height: 30, fontSize: 11.5 },
};
// Input nativo (Box component="input") en vez de TextField variant="standard" para
// las celdas de precio sin label — mismo criterio que AsignarEquiposVariosOT.
const inputCompactoSx = {
  fontFamily: "inherit",
  fontSize: 11,
  padding: "2px 2px",
  border: "none",
  borderBottom: "1px solid",
  borderColor: "divider",
  outline: "none",
  bgcolor: "transparent",
  color: "text.primary",
  "&:focus": { borderColor: "primary.main" },
  "&:disabled": { color: "text.disabled" },
};

const PAGE_SIZE = 15;
const TIPO_LABEL = { Radio: "Radios", Bateria: "Baterías", Modem: "Módems", Chip: "Chips" };
const FAMILIA_LABEL = { RADIO: "Radio", BATERIA: "Batería", MODEM: "Módem", CHIP: "Chip", VARIOS: "Equipo" };

const familiaDe = (t) => ({ Radio: "RADIO", Bateria: "BATERIA", Modem: "MODEM", Chip: "CHIP" }[t] || "VARIOS");

const endpointDeTipo = (tipo) => {
  if (tipo === "Radio") return "/api/equipos-radio";
  if (tipo === "Bateria") return "/api/equipos-bateria";
  if (tipo === "Modem") return "/api/equipos-modem";
  if (tipo === "Chip") return "/api/equipos-chip";
  return "/api/equipos-varios";
};

const VenderEquipoActivoOT = ({ cod_ot, cod_cliente, cod_servicio_cliente, servicio, esTKR, esAVL, onEnviado, onVendidosCambiados }) => {
  const [tiposOtros, setTiposOtros] = useState([]);
  const tipos = esTKR ? ["Radio", "Bateria"] : esAVL ? ["Modem", "Chip"] : tiposOtros;
  const [tipo, setTipo] = useState("");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [buscar, setBuscar] = useState("");
  const [buscarAplicado, setBuscarAplicado] = useState("");
  const [loadingEquipos, setLoadingEquipos] = useState(true);
  const [error, setError] = useState("");

  // { [key]: { tipoEquipo, identificador, precioVenta } }
  const [seleccion, setSeleccion] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [errores, setErrores] = useState([]);
  const [equipoDialog, setEquipoDialog] = useState(null);
  const [confirmarOpen, setConfirmarOpen] = useState(false);

  // Equipos ya vendidos en esta OT (confirmados en una tanda anterior).
  const [vendidos, setVendidos] = useState([]);
  const [loadingVendidos, setLoadingVendidos] = useState(true);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const cargarVendidos = () => {
    setLoadingVendidos(true);
    axios.get(`/api/ot-venta-activos/vendidos/${cod_ot}`)
      .then((res) => setVendidos(res.data || []))
      .catch(() => setVendidos([]))
      .finally(() => setLoadingVendidos(false));
  };

  useEffect(() => { cargarVendidos(); }, [cod_ot]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reporta el conteo al padre para que pueda bloquear "Finalizar" mientras no se vendió
  // nada todavía — mismo patrón que VenderEquipoOT.jsx.
  useEffect(() => {
    if (onVendidosCambiados) onVendidosCambiados(vendidos.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendidos]);

  // "Otros" servicios (incluye POC — legacy tampoco lo trata como TKR acá, cae al mismo
  // lookup dinámico de tipos por servicio): catálogo de tipos dinámico, igual que
  // VenderEquipoOT.jsx.
  useEffect(() => {
    if (esTKR || esAVL) return;
    axios.get("/api/equipos-varios/tipos", { params: { servicio } })
      .then((res) => setTiposOtros(res.data || []))
      .catch(() => setTiposOtros([]));
  }, [esTKR, esAVL, servicio]);

  useEffect(() => {
    if (tipos.length && !tipo) setTipo(tipos[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipos.join("|")]);

  const cargarPrecioSugerido = async (fam) => {
    try {
      const { data } = await axios.get("/api/ot-venta-activos/precio-sugerido", {
        params: { cod_servicio_cliente, familia: fam },
      });
      return Number(data?.precio || 0);
    } catch {
      return 0;
    }
  };

  // Equipo Activo de ESTE cliente (no stock de la sucursal) — sin filtro de ciudad, a
  // diferencia de VenderEquipoOT.jsx (acá lo que importa es de quién es, no dónde está).
  const cargarPagina = (nextPage = page, buscarTexto = buscarAplicado) => {
    if (!tipo) return;
    setLoadingEquipos(true);
    setError("");
    const params = { estado: "Activo", cod_servicio: cod_servicio_cliente, buscar: buscarTexto, page: nextPage, pageSize: PAGE_SIZE };
    // /api/equipos-varios necesita `tipo` para distinguir entre los sub-tipos dinámicos
    // (Accesorio POC, CLIP ENRICO, etc.) — los demás endpoints ya son específicos por
    // familia (equipos-radio, equipos-modem, ...) y no lo usan.
    if (!["Radio", "Bateria", "Modem", "Chip"].includes(tipo)) params.tipo = tipo;
    axios
      .get(endpointDeTipo(tipo), { params })
      .then((res) => {
        const data = res.data || {};
        const items = data.items || data.rows || [];
        const identificadorDe = (r) => r.serie ?? r.num_sim ?? r.numero;
        setItems(items.map((r) => ({
          key: `${tipo.toLowerCase()}-${r.ID ?? r.ID_A ?? identificadorDe(r)}`,
          tipoEquipo: tipo,
          identificador: identificadorDe(r),
          modelo: r.modelo,
          ciudad: r.ciudad,
        })));
        setTotal(Number(data.total ?? 0));
        setPage(Number(data.page || nextPage));
      })
      .catch((err) => {
        console.error("Error al cargar equipo Activo", err);
        setError("No se pudo cargar el equipo disponible.");
      })
      .finally(() => setLoadingEquipos(false));
  };

  useEffect(() => {
    setBuscar("");
    setBuscarAplicado("");
    cargarPagina(1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, cod_servicio_cliente]);

  const handleBuscar = () => {
    setBuscarAplicado(buscar.trim());
    cargarPagina(1, buscar.trim());
  };

  const toggleEquipo = async (row) => {
    if (seleccion[row.key]) {
      setSeleccion((prev) => {
        const { [row.key]: _quitado, ...resto } = prev;
        return resto;
      });
      return;
    }
    const precioSugerido = await cargarPrecioSugerido(familiaDe(row.tipoEquipo));
    setSeleccion((prev) => ({
      ...prev,
      [row.key]: { tipoEquipo: row.tipoEquipo, identificador: row.identificador, modelo: row.modelo, precioVenta: precioSugerido },
    }));
  };

  const quitarSeleccion = (key) => {
    setSeleccion((prev) => {
      const { [key]: _quitado, ...resto } = prev;
      return resto;
    });
  };

  const setPrecio = (key, valor) => {
    setSeleccion((prev) => ({ ...prev, [key]: { ...prev[key], precioVenta: valor } }));
  };

  const seleccionadosEntries = Object.entries(seleccion);
  const seleccionados = Object.values(seleccion);

  const handleConfirmarVenta = async () => {
    if (!seleccionados.length) return;
    setEnviando(true);
    setErrores([]);
    const fallidos = [];
    const exitosos = [];
    for (const [key, item] of Object.entries(seleccion)) {
      try {
        await axios.post("/api/ot-venta-activos/vender", {
          cod_ot,
          familia: familiaDe(item.tipoEquipo),
          identificador: item.identificador,
          cod_cliente,
          cod_servicio_cliente,
          precioVenta: Number(item.precioVenta) || 0,
          tipoEquipoVarios: item.tipoEquipo,
        });
        exitosos.push(key);
      } catch (err) {
        fallidos.push(err?.response?.data?.error || `Error al vender "${item.identificador}"`);
      }
    }
    if (exitosos.length) {
      setSeleccion((prev) => {
        const next = { ...prev };
        exitosos.forEach((k) => delete next[k]);
        return next;
      });
      cargarPagina(page, buscarAplicado);
      cargarVendidos();
      if (onEnviado) onEnviado();
    }
    setErrores(fallidos);
    setEnviando(false);
    if (!fallidos.length) setConfirmarOpen(false);
  };

  return (
    <Box sx={{ mt: 1 }}>
      {error && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{error}</Typography>}

      {!loadingVendidos && !!vendidos.length && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
            Equipos Vendidos en esta OT
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220, borderRadius: 0.5 }}>
            <Table size="small" stickyHeader sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Tipo</TableCell>
                  <TableCell sx={headCellSx}>Identificador</TableCell>
                  <TableCell sx={headCellSx}>Modelo</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 130 }} align="right">Precio de venta</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vendidos.map((r) => (
                  <TableRow key={`${r.familia}-${r.identificador}`} hover>
                    <TableCell>{FAMILIA_LABEL[r.familia] || r.familia}</TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        onClick={() => setEquipoDialog({ tipo: r.familia.toLowerCase(), identificador: r.identificador })}
                        sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
                      >
                        {r.identificador}
                      </Box>
                    </TableCell>
                    <TableCell>{r.modelo}</TableCell>
                    <TableCell align="right">{Number(r.precio_venta || 0).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {tipos.length > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
          <ToggleButtonGroup size="small" exclusive color="primary" value={tipo} onChange={(e, nuevo) => nuevo && setTipo(nuevo)} sx={{ height: 30 }}>
            {tipos.map((t) => (
              <ToggleButton key={t} value={t} sx={{ fontSize: 11.5, px: 1.5, py: 0.35, textTransform: "none" }}>
                {TIPO_LABEL[t] || t}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mb: 1, rowGap: 1 }}>
        <TextField
          size="small"
          label="Buscar"
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleBuscar(); }}
          sx={{ minWidth: 160, ...controlSx }}
        />
        <IconButton size="small" onClick={handleBuscar} title="Buscar"><SearchIcon fontSize="small" /></IconButton>
        <Chip size="small" label={`Total: ${total}`} sx={{ height: 20, fontSize: 11 }} />
        <Chip size="small" color="warning" variant="outlined" label={`Seleccionados: ${seleccionados.length}`} sx={{ height: 20, fontSize: 11 }} />
      </Stack>

      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
        Equipos Activos Disponibles para la Venta
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 0.5, maxHeight: 360, borderRadius: 0.5 }}>
        <Table size="small" stickyHeader sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, width: 32 }}>Sel.</TableCell>
              <TableCell sx={headCellSx}>Identificador</TableCell>
              <TableCell sx={headCellSx}>Modelo</TableCell>
              <TableCell sx={{ ...headCellSx, width: 140 }}>Precio de venta</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingEquipos ? (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 2 }}><CircularProgress size={18} /></TableCell></TableRow>
            ) : !items.length ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    No hay {(TIPO_LABEL[tipo] || tipo || "").toLowerCase()} Activo(a)s de este cliente.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((row) => {
                const datos = seleccion[row.key];
                const checked = Boolean(datos);
                return (
                  <TableRow key={row.key} hover selected={checked}>
                    <TableCell>
                      <Checkbox size="small" checked={checked} onChange={() => toggleEquipo(row)} sx={{ p: 0 }} />
                    </TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        onClick={() => setEquipoDialog({ tipo: row.tipoEquipo.toLowerCase(), identificador: row.identificador })}
                        sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
                      >
                        {row.identificador}
                      </Box>
                    </TableCell>
                    <TableCell>{row.modelo}</TableCell>
                    <TableCell>
                      <Box
                        component="input"
                        type="number"
                        disabled={!checked}
                        value={datos?.precioVenta ?? ""}
                        onChange={(e) => setPrecio(row.key, e.target.value)}
                        sx={{ ...inputCompactoSx, width: 90 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          Página {total ? page : 0} / {total ? totalPages : 0}
        </Typography>
        <Button size="small" variant="outlined" startIcon={<NavigateBeforeIcon />} disabled={loadingEquipos || page <= 1}
          onClick={() => cargarPagina(page - 1)} sx={{ minHeight: 26, height: 26, fontSize: 11 }}>
          Anterior
        </Button>
        <Button size="small" variant="outlined" endIcon={<NavigateNextIcon />} disabled={loadingEquipos || page >= totalPages}
          onClick={() => cargarPagina(page + 1)} sx={{ minHeight: 26, height: 26, fontSize: 11 }}>
          Siguiente
        </Button>
      </Stack>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          size="small"
          variant="contained"
          color="success"
          disabled={!seleccionados.length}
          onClick={() => setConfirmarOpen(true)}
          sx={{ minHeight: 28, height: 28, fontSize: 11.5, px: 2 }}
        >
          Vender Equipos Activos Seleccionados
        </Button>
      </Box>

      <Dialog open={confirmarOpen} onClose={() => !enviando && setConfirmarOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Confirmar Venta de Equipos Activos</DialogTitle>
        <DialogContent>
          {!!errores.length && (
            <MuiAlert severity="error" variant="outlined" sx={{ mb: 1, fontSize: 11, py: 0 }}>
              {errores.map((msg, i) => (<div key={i}>{msg}</div>))}
            </MuiAlert>
          )}
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320, borderRadius: 0.5 }}>
            <Table size="small" stickyHeader sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Tipo</TableCell>
                  <TableCell sx={headCellSx}>Identificador</TableCell>
                  <TableCell sx={headCellSx}>Modelo</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 130 }}>Precio de venta</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 40 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {seleccionadosEntries.map(([key, datos]) => (
                  <TableRow key={key} hover>
                    <TableCell>{TIPO_LABEL[datos.tipoEquipo] || datos.tipoEquipo}</TableCell>
                    <TableCell>{datos.identificador}</TableCell>
                    <TableCell>{datos.modelo}</TableCell>
                    <TableCell>
                      <Box
                        component="input"
                        type="number"
                        value={datos.precioVenta ?? ""}
                        onChange={(e) => setPrecio(key, e.target.value)}
                        sx={{ ...inputCompactoSx, width: 90 }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => quitarSeleccion(key)} title="Quitar">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setConfirmarOpen(false)} disabled={enviando}>Cancelar</Button>
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={!seleccionados.length || enviando}
            onClick={handleConfirmarVenta}
          >
            {enviando ? <CircularProgress size={16} color="inherit" /> : "Confirmar Venta"}
          </Button>
        </DialogActions>
      </Dialog>

      <CentroEquipoDetalleDialog
        open={!!equipoDialog}
        tipo={equipoDialog?.tipo}
        identificador={equipoDialog?.identificador}
        onClose={() => setEquipoDialog(null)}
      />
    </Box>
  );
};

export default VenderEquipoActivoOT;
